import { getActiveLocale } from '@/i18n';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { METRIC } from '@/services/directOrderService';

// Generic display formatters shared across every screen (order-tracking, catchment area, sales, etc).
//
// Callers decide what an undefined figure looks like by NOT calling these at all - once a value reaches
// here, `null`/non-finite always renders as EMPTY_DISPLAY, never as "0" or "0 %".

export const EMPTY_DISPLAY = '—';

export function getCurrencySymbol() {
	return iframeDataBridge.getIframeWindow()?.externis?.sales?.salesHelper?.getCurrency?.() || '€';
}

export function formatNumber(value, { decimals = 0, locale = getActiveLocale() } = {}) {
	if (value == null || !Number.isFinite(Number(value))) return EMPTY_DISPLAY;
	return new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(Number(value));
}

// `compact` is opt-in (space-constrained callers only, e.g. the order-tracking widget's hero KPI) -
// existing call sites keep their exact figure unless they ask for this. Weight never goes through
// this path (formatWeight has no `compact`), matching the ask that only currency collapses to "1K".
export function formatValue(value, { decimals = 0, currency = getCurrencySymbol(), locale = getActiveLocale(), compact = false } = {}) {
	if (value == null || !Number.isFinite(Number(value))) return EMPTY_DISPLAY;
	const numeric = Number(value);
	if (compact && Math.abs(numeric) >= 1000) {
		const thousands = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(numeric / 1000);
		return `${thousands}K ${currency}`.trim();
	}
	return `${formatNumber(numeric, { decimals, locale })} ${currency}`.trim();
}

export function formatWeight(value, { decimals = 0, locale = getActiveLocale() } = {}) {
	if (value == null || !Number.isFinite(Number(value))) return EMPTY_DISPLAY;
	return `${formatNumber(value, { decimals, locale })} Kg`;
}

export function formatMetric(value, metric, options = {}) {
	return metric === METRIC.WEIGHT ? formatWeight(value, options) : formatValue(value, options);
}

// Takes a 0..1 ratio as produced by safeRatio, not an already-multiplied percentage.
export function formatPercent(ratio, { decimals = 0, locale = getActiveLocale() } = {}) {
	if (ratio == null || !Number.isFinite(Number(ratio))) return EMPTY_DISPLAY;
	return `${formatNumber(Number(ratio) * 100, { decimals, locale })} %`;
}

export function formatPricePerKg(value, { currency = getCurrencySymbol(), locale = getActiveLocale() } = {}) {
	if (value == null || !Number.isFinite(Number(value))) return EMPTY_DISPLAY;
	return `${formatNumber(value, { decimals: 2, locale })} ${currency}/Kg`;
}

export function formatDate(value, locale = getActiveLocale()) {
	if (!value) return '';
	const date = new Date(value);
	return isNaN(date.getTime()) ? '' : date.toLocaleDateString(locale);
}

export function formatPeriodLabel(startDate, endDate, locale = getActiveLocale()) {
	if (!startDate || !endDate) return '';
	const format = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' });
	return `${format.format(new Date(startDate))} → ${format.format(new Date(endDate))}`;
}

export function formatMonthLabel(key, locale = getActiveLocale()) {
	const [year, month] = String(key).split('-').map(Number);
	if (!year || !month) return key;
	return new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit' }).format(new Date(year, month - 1, 1));
}
